import { DatatableWrapper, TableBody, TableHeader, Pagination, PaginationOptions } from "react-bs-datatable";
import { Table, Row, Col, Container, Button, Form, Modal, Badge } from "react-bootstrap";
import { Fragment, useEffect, useState } from "react";
import HRMSApi from "../../api/HRMSApi";
import jwtDecode from "jwt-decode";
import { toast, ToastContainer } from "react-toastify";
import LeaveStatusUpdate from "../my_leaves/LeaveStatusUpdate";
import LeavesTableSection from "./LeavesTableSection";
import LeaveRejectTable from "./LeaveRejectTable";

const TeamLeaves = () => {
    const [searchApprovedLeaves, setSearchApprovedLeaves] = useState("");
    const [searchPendingLeaves, setSearchPendingLeaves] = useState("");
    const [searchRejectedLeaves, setSearchRejectedLeaves] = useState("");
    const [requestLeaves, setRequestLeaves] = useState([]);
    const [shimmer, setShimmer] = useState(false);
    const [userId, setUserId] = useState();
    const [reloadTable, setReloadTable] = useState(false);

    const fetchTeamLeaves = async () => {
        setShimmer(true);
        let response = await HRMSApi.getApprovalRequest();
        console.log('response', response);
        if (response.success) {
            setRequestLeaves(response.records);
        }
        setShimmer(false);
    };

    const getLoggedInUser = () => {
        let userDetails = jwtDecode(localStorage.getItem('token'));
        setUserId(userDetails.contactDetails.Id);
    };

    useEffect(() => {
        fetchTeamLeaves();
        getLoggedInUser();
    }, [reloadTable]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const d = new Date(dateString);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const [isConfirmationModal, setIsConfirmationModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [pendingRecord, setPendingRecord] = useState();


    const updateStatus = async (payLoadInfo) => {
        setShimmer(true);
        setIsConfirmationModal(true);

        try {
            const result = await HRMSApi.updateApprovalRequest(payLoadInfo);

            if (!result || !result.success) {
                toast.error(result?.message || "Failed to update leave request(s).");
                fetchTeamLeaves();
                return;
            }

            if (result.totalFailed === 0) {
                setShimmer(false);
                if (payLoadInfo.rejectionReasons) {
                    toast.success(`${result.totalProcessed} leave(s) rejected successfully!`);
                } else {
                    toast.success(`${result.totalProcessed} leave(s) approved successfully!`);
                }
            } else {
                setShimmer(false);
                toast.warning(
                    `${result.totalProcessed} leave(s) updated. ` +
                    `${result.totalFailed} failed.`
                );
            }
            fetchTeamLeaves();

        } catch (err) {
            toast.error(err.message || "An error occurred while updating the request.");
        } finally {
            fetchTeamLeaves();
            setShimmer(false);
            setReloadTable(true);
            setIsConfirmationModal(false);
        }
    };


    const handleApproveSubmit = () => {
        updateStatus({ "leaveIds": selectedRows, status: "Approved" });
        setShowApproveModal(false);
    };

    const processedBody = requestLeaves.map((item, index) => ({
        ...item,
        srno: index + 1,
        startDate: formatDate(item.startDate)
    }));

    const pendingLeaves = processedBody.filter(
        item => item.status === "Pending for Approval"
    );

    const approvedLeaves = processedBody.filter(
        item => item.status === "Approved"
    );

    const rejectedLeaves = processedBody.filter(
        item => item.status === "Rejected"
    );

    const applyApprovedSearch = (data) => {
        return data.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchApprovedLeaves.toLowerCase())
            )
        );
    }

    const applyPendingSearch = (data) => {
        return data.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchPendingLeaves.toLowerCase())
            )
        );
    }

    const applyRejectedSearch = (data) => {
        return data.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchRejectedLeaves.toLowerCase())
            )
        );
    }

    const handleRowSelect = (id, checked) => {
        setSelectedRows(prev =>
            checked ? [...prev, id] : prev.filter(rowId => rowId !== id)
        );
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(pendingLeaves.map(item => item.id));
        } else {
            setSelectedRows([]);
        }
    };

    const pendingLeaveHeader = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                />
            ),
            prop: "select",
            cell: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                />
            ),
            isSortable: false,
            isFilterable: false
        },
        { title: "Start Date", prop: "startDate", isFilterable: true, isSortable: true },
        { title: "Employee", prop: "employeeName", isFilterable: true, isSortable: true },
        { title: "Type", prop: "leaveType", isFilterable: true, isSortable: true },
        { title: "Reason", prop: "reason", isFilterable: true, isSortable: true },
        {
            title: "Comments",
            prop: "comment",
            isFilterable: true,
            isSortable: true,
            cell: (row) => (
                <div
                    title={row.comment}
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        maxWidth: "170px",
                        cursor: "pointer"
                    }}
                >
                    {row.comment}
                </div>
            )
        }
    ];

    const approvedLeavesHeader = [
        { title: "Start Date", prop: "startDate", isFilterable: true, isSortable: true },
        { title: "Employee", prop: "employeeName", isFilterable: true, isSortable: true },
        { title: "Type", prop: "leaveType", isFilterable: true, isSortable: true },
        { title: "Final Status", prop: "finalStatus", isFilterable: true, isSortable: true, cell: (row) => <Badge bg={row.finalStatus === 'Rejected' ? "danger" : row.finalStatus === 'Approved' ? "success" : row.finalStatus === "Revoked" ? "secondary" : "warning"}>{row.finalStatus}</Badge> },
        {
            title: "Comments",
            prop: "comment",
            isFilterable: true,
            isSortable: true,
            cell: (row) => (
                <div
                    title={row.comment}
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        maxWidth: "170px",
                        cursor: "pointer"
                    }}
                >
                    {row.comment}
                </div>
            )
        }
    ]

    const rejctedLeavesHeader = [
        { title: "Start Date", prop: "startDate", isFilterable: true, isSortable: true },
        { title: "Employee", prop: "employeeName", isFilterable: true, isSortable: true },
        { title: "Type", prop: "leaveType", isFilterable: true, isSortable: true },
        { title: "Final Status", prop: "finalStatus", isFilterable: true, isSortable: true, cell: (row) => <Badge bg={row.finalStatus === 'Rejected' ? "danger" : row.finalStatus === 'Approved' ? "success" : row.finalStatus === "Revoked" ? "secondary" : "warning"}>{row.finalStatus}</Badge> },
        { title: "Reason", prop: "reason", isFilterable: true, isSortable: true },
        {
            title: "Comments",
            prop: "comment",
            isFilterable: true,
            isSortable: true,
            cell: (row) => (
                <div
                    title={row.comment}
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        maxWidth: "170px",
                        cursor: "pointer"
                    }}
                >
                    {row.comment}
                </div>
            )
        }
    ]

    const handleApproveLeaves = () => {
        if (!selectedRows || selectedRows.length === 0) {
            toast.error("Please select at least one leave request to approve.");
            return;
        }

        setShowApproveModal(true);
    };


    const handleRejectLeaves = () => {
        const selectionCheck = pendingLeaves.filter((item) =>
            selectedRows.includes(item.id)
        );

        if (selectionCheck.length === 0) {
            toast.error("Please select at least one leave request to reject.");
            return;
        }

        setPendingRecord(selectionCheck);
        setShowRejectionModal(true);
    };


    const handleApproveRequest = () => {
        handleApproveSubmit();
    }

    const labels = { beforeSelect: " " }

    return (
        <Fragment>
            <Container className='mt-5'>
                <Row className='justify-content-center'>
                    <Col lg={10}>
                        <div className='text-center p-2' style={{ height: '40px', backgroundColor: '#ffffff', borderRadius: '5px' }}>
                            <span className='fw-semibold' style={{ color: '#605C68', fontSize: 'large' }}>
                                Team Leaves
                            </span>
                        </div>
                    </Col>
                </Row>
            </Container>

            {!shimmer ? (
                <Container className="mt-3">
                    <Row className='justify-content-center'>
                        <Col lg={10} style={{ paddingBottom: "10px" }}>

                            {/* Search */}
                            <Row className="d-flex mb-3 justify-content-end">
                                <Col xs={4}>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white">
                                            <i className="fa fa-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search leaves..."
                                            value={searchPendingLeaves}
                                            onChange={(e) => setSearchPendingLeaves(e.target.value)}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {/* Table */}

                            <Container>
                                <Row>
                                    <Col lg={10} className="p-0">
                                        <h5 className="mb-3 d-flex align-items-center">
                                            Pending Leaves
                                            <i
                                                className="fa fa-info-circle ms-2 text-secondary"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="right"
                                                title="Below information displays pending leaves."
                                                style={{ cursor: "pointer" }}
                                            ></i> &nbsp;
                                            ({pendingLeaves.length})
                                        </h5>
                                    </Col>
                                    <Col lg={2} className="d-flex justify-content-end align-items-center m-0 p-0">
                                        <button className="btn btn-sm btn-success mx-2" onClick={handleApproveLeaves}>Approve</button>
                                        <button className="btn btn-sm btn-danger" onClick={handleRejectLeaves}>Reject</button>
                                    </Col>
                                </Row>
                            </Container>

                            <DatatableWrapper
                                headers={pendingLeaveHeader}
                                body={applyPendingSearch(pendingLeaves)}
                                sortProps={{
                                    initialState: {
                                        sortBy: []
                                    }
                                }}
                                paginationOptionsProps={{ initialState: { rowsPerPage: 5, options: [5, 10, 15, 20, 25, 30] } }}
                            >
                                <Table striped className="data-table">
                                    <TableHeader />
                                    <TableBody />
                                </Table>

                                <Row>
                                    <Col lg={6} xs={12} sm={12} className="d-flex align-items-center justify-content-start">
                                        <span className='me-2 mt-2'>
                                            Show
                                        </span>
                                        <PaginationOptions labels={labels} />
                                        <div className='mt-2 mx-2'>
                                            Records
                                        </div>
                                    </Col>

                                    <Col lg={6} xs={12} sm={12} className='d-flex align-items-center justify-content-end'>
                                        <div className='text-end mb-1 mt-3'>
                                            <Pagination />
                                        </div>
                                    </Col>
                                </Row>
                            </DatatableWrapper>
                        </Col>
                    </Row>

                    {/* Approved Leaves  */}


                    <LeavesTableSection
                        title="Approved Leaves"
                        tooltip="Below information displays approved leaves."
                        searchValue={searchApprovedLeaves}
                        onSearchChange={setSearchApprovedLeaves}
                        headers={approvedLeavesHeader}
                        body={applyApprovedSearch(approvedLeaves)}
                        labels={labels}
                    />

                    {/* Rejcted Leaves */}


                    <LeavesTableSection
                        title="Rejected Leaves"
                        tooltip="Below information displays rejected leaves."
                        searchValue={searchRejectedLeaves}
                        onSearchChange={setSearchRejectedLeaves}
                        headers={rejctedLeavesHeader}
                        body={applyRejectedSearch(rejectedLeaves)}
                        labels={labels}
                    />


                    {/* Reject Modal */}
                    <LeaveRejectTable setShimmer={setShimmer} pendingRecord={pendingRecord} fetchTeamLeaves={fetchTeamLeaves} setPendingRecord={setPendingRecord} showRejectionModal={showRejectionModal} setShowRejectionModal={setShowRejectionModal} />

                </Container>
            ) : (
                <div className="sk-cube-grid">
                    <div className="sk-cube sk-cube1"></div>
                    <div className="sk-cube sk-cube2"></div>
                    <div className="sk-cube sk-cube3"></div>
                    <div className="sk-cube sk-cube4"></div>
                    <div className="sk-cube sk-cube5"></div>
                    <div className="sk-cube sk-cube6"></div>
                    <div className="sk-cube sk-cube7"></div>
                    <div className="sk-cube sk-cube8"></div>
                    <div className="sk-cube sk-cube9"></div>
                </div>
            )}

            <ToastContainer />

            <LeaveStatusUpdate
                show={showApproveModal}
                onHide={() => setShowApproveModal(false)}
                handleApproveRequest={handleApproveRequest}
                title="Confirmation Approval"
                table="request"
            />

        </Fragment>
    );
};

export default TeamLeaves;